import {
  IDatabasePropertyResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'

import {
  IStatusPropertyTypeInterfaces,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/StatusPropertyTypeStrategy/types/IStatusPropertyTypeInterfaces'
import {DatabaseViewType} from '@toolify/server/src/models/mongoose/DatabaseViewModel/enum/DatabaseViewType'
import {useStatusApiStore} from '@toolify/client/src/stores/api/StatusApiStore/useStatusApiStore'
import {useParticularColors} from '@toolify/client/src/modules/color/composables/useParticularColors'
import {FilterPropertyOperator} from '@toolify/server/src/services/FilterService/enum/FilterPropertyOperator'
import {FilterItemType} from '@toolify/server/src/services/FilterService/enum/FilterItemType'
import {FilterPropertyPayloadType} from '@toolify/server/src/services/FilterService/enum/FilterPropertyPayloadType'
import {PropertyStrategyComponentType} from '@toolify/client/src/modules/tableItems/enum/PropertyStrategyComponentType'
import {usePropertyTypeStrategy} from '@toolify/client/src/modules/databases/strategies/PropertyTypeStrategy/usePropertyTypeStrategy'
import {FilterGroupOperator} from '@toolify/server/src/services/FilterService/enum/FilterGroupOperator'
import {IFilter} from '@toolify/server/src/services/FilterService/types/IFilter'
import {IFilterPropertyItem} from '@toolify/server/src/services/FilterService/types/IFilterPropertyItem'
import {GroupSortModeType} from '@toolify/server/src/services/DatabasePropertyService/enum/GroupSortModeType'
import {COMMON_GROUP_SORT_LABEL_KEYS} from '@toolify/client/src/modules/databases/utils/groupSortModes'
import {GroupGranularityType} from '@toolify/server/src/services/DatabasePropertyService/types/GroupGranularityType'
import {StatusGroupGranularityType} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/StatusPropertyTypeStrategy/enum/StatusGroupGranularityType'
import {
  isStatusGroupBucket,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/StatusPropertyTypeStrategy/statusGroupBucketing'
import {
  statusGroupGranularityDescriptor,
} from '@toolify/client/src/modules/databases/utils/statusGroupGranularityDescriptor'
import {resolveStatusSubPropertyValue} from '@toolify/client/src/modules/databases/utils/resolveStatusSubPropertyValue'

export function useStatusValueTypeStrategy() {
  const statusApiStore = useStatusApiStore()
  const {getColorByParticularColor} = useParticularColors()

  const strategyComponentType = PropertyStrategyComponentType.Dropdown

  const fullValueToText = (value: IStatusPropertyTypeInterfaces['fullValue'], property: IDatabasePropertyResponseInstance) => {
    if(!value) {
      return ''
    }
    return value.title
  }

  const fullValueToColor = (value: IStatusPropertyTypeInterfaces['fullValue'], property?: IDatabasePropertyResponseInstance):{color: string, colorKey:string | null} | null  => {
    if(!value) {
      return null
    }
    const statusKey = value.id || value.title || ''
    return {
      color: getColorByParticularColor(value.color),
      colorKey: property ? `${property.id}-${statusKey}` : null,
    }
  }

  const getAvailableValues = async (property: IDatabasePropertyResponseInstance) => {
    const propertyTypeStrategy = usePropertyTypeStrategy(property.type)
    const statuses = await statusApiStore.getStatuses(propertyTypeStrategy.getSourcePropertyId(property))
    return {
      values: statuses,
      isGlobal: false,
    }
  }

  const fullValueToInputValue = (value: IStatusPropertyTypeInterfaces['fullValue']) => {
    if(!value) {
      return null
    }
    return value.id || value.title || null
  }

  const storageValueToInputValue = (value: unknown): IStatusPropertyTypeInterfaces['inputValue'] | null => {
    if(isStatusGroupBucket(value)) {
      return value.statusIds[0] ?? null
    }
    return (value as IStatusPropertyTypeInterfaces['storedValue']) ?? null
  }
  
  const getPossibleValueFilter = (possibleValue: IStatusPropertyTypeInterfaces['fullValue'], group: string) => {
    const filterValue = possibleValue.id || possibleValue.title || null
    return {
      type: FilterItemType.Property,
      id: group,
      operator: {
        type: FilterPropertyOperator.Equals,
      },
      subProperty: null,
      payload: {
        type: FilterPropertyPayloadType.Static,
        value: filterValue,
      },
      isDisabled: false,
    }
  }

  const sortGroupItems = (a: {fullValue: IStatusPropertyTypeInterfaces['fullValue']}, b: {fullValue: IStatusPropertyTypeInterfaces['fullValue']}): number => {
    const statusA = a.fullValue
    const statusB = b.fullValue
    
    if (!statusA) return 1
    if (!statusB) return -1
    
    return (statusA.position || -1) - (statusB.position || -1)
  }

  const compareItemValues = (
    valueA: IStatusPropertyTypeInterfaces['fullValue'],
    valueB: IStatusPropertyTypeInterfaces['fullValue'],
    sortDirection: '1' | '-1',
  ): number => {
    if (!valueA && !valueB) return 0
    if (!valueA) return 1
    if (!valueB) return -1
    
    const positionA = valueA.position ?? -1
    const positionB = valueB.position ?? -1
    const comparison = positionA - positionB
    
    return sortDirection === '1' ? comparison : -comparison
  }

  const updateValueBySingleValueGroupSwitch = (
    currentValue: IStatusPropertyTypeInterfaces['fullValue'],
    oldGroupValue: IStatusPropertyTypeInterfaces['fullValue'],
    newGroupValue: IStatusPropertyTypeInterfaces['fullValue'],
  ): IStatusPropertyTypeInterfaces['inputValue'] | null => {
    if (!newGroupValue) return null
    return newGroupValue.id || newGroupValue.title || null
  }

  const updateFullValueBySingleValueGroupSwitch = (
    currentValue: IStatusPropertyTypeInterfaces['fullValue'],
    oldGroupValue: IStatusPropertyTypeInterfaces['fullValue'],
    newGroupValue: IStatusPropertyTypeInterfaces['fullValue'],
  ): IStatusPropertyTypeInterfaces['fullValue'] | null => {
    return newGroupValue || null
  }

  const searchToFilterQuery = (search: string, databaseProperties: IDatabasePropertyResponseInstance[], databasePropertiesTableDataMap?: Map<string, unknown>): IFilter => {
    const escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const searchRegex = new RegExp(escapedSearch, 'i')
    return {
      type: FilterItemType.Group,
      operator: FilterGroupOperator.Or,
      items: databaseProperties.map(databaseProperty => {
        let statusIds: string[] = []
        if(databasePropertiesTableDataMap?.has(databaseProperty.id)) {
          const statuses = databasePropertiesTableDataMap.get(databaseProperty.id) as Array<{id: string, title?: string}>
          if(statuses && Array.isArray(statuses)) {
            statusIds = statuses
              .filter(status => status.title && searchRegex.test(status.title))
              .map(status => status.id)
          }
        }
        if(!statusIds.length) {
          return null
        }
        return {
          type: FilterItemType.Property,
          id: databaseProperty.id,
          subProperty: null,
          operator: {
            type: FilterPropertyOperator.In,
          },
          payload: {
            type: FilterPropertyPayloadType.Static,
            value: statusIds,
          },
          isDisabled: false,
        } as IFilterPropertyItem
      }).filter((item): item is IFilterPropertyItem => !!item),
    }
  }

  const canGroupByView = (viewType: DatabaseViewType, _property: IDatabasePropertyResponseInstance) =>
    viewType === DatabaseViewType.Table || viewType === DatabaseViewType.Timeline || viewType === DatabaseViewType.Kanban

  const canMoveItemToAnotherGroup = (property: IDatabasePropertyResponseInstance | null | undefined, granularity?: GroupGranularityType | null): boolean => {
    if(property?.canBeModified === false) {
      return false
    }
    return granularity !== StatusGroupGranularityType.StatusGroup
  }

  const canCreateItemInGroup = (property: IDatabasePropertyResponseInstance | null | undefined, _granularity?: GroupGranularityType | null, groupValue?: unknown): boolean => {
    if(property?.canBeModified === false) {
      return false
    }
    return !(isStatusGroupBucket(groupValue) && groupValue.statusIds.length === 0)
  }

  const groupGranularity = statusGroupGranularityDescriptor

  const groupSort = {
    availableModes: [
      GroupSortModeType.Natural,
      GroupSortModeType.ValueAsc,
      GroupSortModeType.ValueDesc,
      GroupSortModeType.CountDesc,
      GroupSortModeType.CountAsc,
    ],
    defaultMode: GroupSortModeType.Natural,
    getLabelKey: (mode: GroupSortModeType): string => COMMON_GROUP_SORT_LABEL_KEYS[mode],
    compareGroupValues: (
      valueA: IStatusPropertyTypeInterfaces['fullValue'],
      valueB: IStatusPropertyTypeInterfaces['fullValue'],
      sortDirection: '1' | '-1',
    ): number => {
      if (!valueA && !valueB) return 0
      if (!valueA) return 1
      if (!valueB) return -1
      const textA = valueA.title ?? ''
      const textB = valueB.title ?? ''
      const comparison = textA.localeCompare(textB)
      return sortDirection === '1' ? comparison : -comparison
    },
  }

  return {
    fullValueToText,
    fullValueToColor,
    fullValueToInputValue,
    storageValueToInputValue,
    resolveSubPropertyValue: resolveStatusSubPropertyValue,
    getAvailableValues,
    getPossibleValueFilter,
    strategyComponentType,
    sortGroupItems,
    compareItemValues,
    updateValueBySingleValueGroupSwitch,
    updateFullValueBySingleValueGroupSwitch,
    searchToFilterQuery,
    canGroupByView,
    canMoveItemToAnotherGroup,
    canCreateItemInGroup,
    groupGranularity,
    groupSort,
  }
}
